import React, { useRef, useEffect, useState } from 'react';
import CLSManager from '../utils/CLSManager';

/**
 * Wrapper component that prevents CLS by applying stored heights
 * Now supports different heights for different viewport sizes
 * Can optionally set height on a parent element
 * Supports adding additional height to the calculated/stored height
 */
export default function StableHeightContainer({ 
  id, 
  children, 
  className = '', 
  targetParent = false, 
  parentSelector = '',
  additionalHeight = 0
}) {
  const containerRef = useRef(null);
  const [currentViewport, setCurrentViewport] = useState('default');
  const storedHeight = CLSManager.getHeight(id, currentViewport);
  
  // Calculate final height with additional pixels if needed
  const finalHeight = storedHeight ? storedHeight + additionalHeight : null;
  
  // Detect viewport changes
  useEffect(() => {
    const updateViewport = () => {
      // Define breakpoints - adjust these values based on your design system
      if (window.innerWidth < 768) {
        setCurrentViewport('mobile');
      } else if (window.innerWidth < 1024) {
        setCurrentViewport('tablet');
      } else {
        setCurrentViewport('desktop');
      }
    };
    
    // Set initial viewport
    updateViewport();
    
    // Listen for resize events
    window.addEventListener('resize', updateViewport);
    
    return () => {
      window.removeEventListener('resize', updateViewport);
    };
  }, []);
  
  useEffect(() => {
    if (!containerRef.current) return;
    
    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        const rawHeight = Math.ceil(entry.contentRect.height);
        if (rawHeight > 10) {
          // Store the raw height without additional height
          CLSManager.setHeight(id, rawHeight, currentViewport);
          
          // Apply height to parent if targetParent is enabled
          if (targetParent && containerRef.current) {
            let targetElement;
            
            if (parentSelector) {
              // Find closest parent matching the selector
              targetElement = containerRef.current.closest(parentSelector);
            } else {
              // Default to immediate parent
              targetElement = containerRef.current.parentElement;
            }
            
            if (targetElement) {
              // Apply height plus additional height only for display
              targetElement.style.minHeight = `${rawHeight + additionalHeight}px`;
            }
          }
        }
      }
    });
    
    resizeObserver.observe(containerRef.current);
    
    return () => {
      resizeObserver.disconnect();
    };
  }, [id, currentViewport, targetParent, parentSelector, additionalHeight]);
  
  // Only apply minHeight style if we're not targeting a parent
  const minHeightStyle = (!targetParent && finalHeight) ? { minHeight: `${finalHeight}px` } : {};
  
  // Apply stored height to parent on mount if targetParent is enabled
  useEffect(() => {
    if (targetParent && storedHeight && containerRef.current) {
      let targetElement;
      
      if (parentSelector) {
        targetElement = containerRef.current.closest(parentSelector);
      } else {
        targetElement = containerRef.current.parentElement;
      }
      
      if (targetElement) {
        // Apply stored height plus additional height for display only
        targetElement.style.minHeight = `${storedHeight + additionalHeight}px`;
      }
    }
  }, [targetParent, parentSelector, storedHeight, additionalHeight]);
  
  return (
    <div 
      ref={containerRef} 
      className={`stable-height-container ${className}`} 
      style={{ minHeight: `${storedHeight}px` }}
      data-component-id={id}
      data-viewport={currentViewport}
      data-target-parent={targetParent ? 'true' : 'false'}
      data-additional-height={additionalHeight}
      data-raw-height={storedHeight || 'not-set'}
      data-final-height={finalHeight || 'not-set'}
    >
      {children}
    </div>
  );
}
